<template>
    <div class="hts-layout-default">
        <template v-if="!initialized">
            <div class="w-screen h-screen">
                <div class="hts-loading loading">
                    <div class="hts-loading__dot-fire" />
                </div>
            </div>
        </template>
        <template v-else>
            <layout-navigation
                :menu-items="menuItems"
                :top-menu-items="topMenuItems">
                <NuxtPage />
            </layout-navigation>
        </template>
        <hts-global-backdrop />
    </div>
</template>

<script lang="ts" setup>
import LayoutNavigation from './-default/navigation.vue'
import { checkRoutePermission } from '@/utils/permission-check'
import menuItems from '~/data/menus/customer-portal/mainMenu.json'
import topMenuItems from '~/data/menus/customer-portal/topMenu.json'

const config = useRuntimeConfig().public
useHead({
  title: config.appName
})

const store = $store()
const initialized = ref(false)
const { colors } = useRuntimeConfig().public
const themeColor = store.theme.getColor
const router = useRouter()
const workspaceId = computed(() => store.auth.workspaceId)
const companyId = computed(() => store.system.getCompanyId)
const companyType = computed(() => store.auth.companyType)
const id = computed(() => router.currentRoute.value.params.id)
const route: any = computed(() => router.currentRoute.value)
const { $event, $vehicle } = useNuxtApp()

const notipushed = ref<string[]>([])

onMounted(async () => {
  themeColor !== 'orange' && setThemeColor()
  if (!id.value) {
    return
  }
  if (id.value !== workspaceId.value) {
    return navigateTo({
      // name: 'workspace-list'
      name: 'user-search-booking'
    })
  }
  if (!checkOwner(companyType.value)) {
    return
  }
  await getSystemConfig()
  nextTick(() => {
    initialized.value = true
  })
  useState('initialized', () => true)
  checkRoutePermission((route.value?.name || '').toString(), true)
  notificationListen()
  getNotifications()
  getGlobalSettings()
})

onUnmounted(() => {
  const user: any = store.userConfig.user
  $event.unsubscribe('company-user-notification-' + user._id, () => {})
  $event.unsubscribe('company-sync', () => {})

  store.notification.clear()
})

watch(
  () => companyType.value,
  (val: string | undefined) => {
    checkOwner(val)
  }
)

const setThemeColor = () => {
  if (process.client && colors[themeColor as keyof typeof colors]) {
    const color = colors[themeColor as keyof typeof colors]
    const r: HTMLElement | null = document.querySelector(':root')
    if (r) {
      r.style.setProperty('--primary-main-50', color['50'])
      r.style.setProperty('--primary-main-100', color['100'])
      r.style.setProperty('--primary-main-200', color['200'])
      r.style.setProperty('--primary-main-300', color['300'])
      r.style.setProperty('--primary-main-400', color['400'])
      r.style.setProperty('--primary-main-500', color['500'])
      r.style.setProperty('--primary-main-600', color['600'])
      r.style.setProperty('--primary-main-700', color['700'])
      r.style.setProperty('--primary-main-800', color['800'])
      r.style.setProperty('--primary-main-900', color['900'])
    }
  }
}

const notificationListen = async () => {
  if (process.client) {
    const user: any = store.userConfig.user
    await $event.connect()
    $event.listen('company-user-notification-' + user._id, (data: any) => {
      if (!notipushed.value.includes(data?.message?._id)) {
        store.notification.unshiftNotification(data?.message || null)
        store.notification.plusUnread(1)
        notipushed.value.push(data)
      }
    })

    $event.listen('company-sync', async ({ message }: any) => {
      if (!message) {
        return
      }
      if (message?.companyId === companyId.value) {
        switch (message?.type) {
        case 'system-data':
          await getSystemData()
          break
        case 'global-settings':
          getGlobalSettings()
          break
        case 'vehicle':
          $vehicle.setGPS(message.info)
          break
        }
      }
    })
  }
}

const getSystemConfig = async () => {
  try {
    const response = await $api($url.system.getSystemConfig)

    const responseData: any = $lodash.get(response, 'data.value')
    if (responseData?.success) {
      const configData = responseData.data || {}
      const user = configData.user || {}
      const group = configData.group || {}
      store.system.setCompany(user.company)
      store.system.setWorkspaceCompany(configData.workspaceCompany)
      store.system.setWorkspace(user.workspace)
      store.system.setWorkspaceUsers(configData.workspaceUsers)
      store.system.setCompanyUsers(configData.companyUsers)
      store.system.setPrimaryColor(configData.primaryColor)
      store.system.setGroup(user.group)
      store.system.timezone = configData.timezone
      store.system.dateFormat = configData.dateFormat
      store.system.timeFormat = configData.timeFormat
      store.system.timezoneText = configData.timezoneText
      store.userConfig.setPage('PARTNER')
      store.userConfig.setUser(user)
      store.userConfig.setGroup(group)
      store.userConfig.setAvatar(user.avatar)
      store.userConfig.setLogo(user.company && user.company.logoInfo?.path)
      store.userConfig.workspaces = configData.workspaces || []
      store.userConfig.companyTypes = configData.companyTypes || []
      store.userConfig.isAdmin = configData.isAdmin || false
      store.userConfig.isCompanyAdmin = configData.isCompanyAdmin || false
      store.data.setBusinesses(configData.businesses || [])
      store.data.setVehicleCategories(configData.vehicleCategories || [])
      store.data.setTasks(configData.tasks || [])
      store.data.setDrivers(configData.drivers || [])

      useState('group', () => user.group)
    } else {
      router.push({ name: 'errors-access-denied' })
    }
  } catch (e: any) {
    if (process.client && typeof e.message === 'string') {
      $toast().error(e.message)
    }
  }
}

const getSystemData = $lodash.debounce(async () => {
  try {
    const response = await $api($url.system.getSystemData)
    const responseData: any = $lodash.get(response, 'data.value')
    if (responseData.success) {
      const configData = responseData.data || {}

      store.data.setBusinesses(configData.business)
      store.data.setTasks(configData.task)
    }
  } catch (e: any) {
    if (process.client && typeof e.message === 'string') {
      $toast().error(e.message)
    }
  }
}, 200)

const getGlobalSettings = () => {
  const api = $url.settings.getGlobalSettings
  $api(api).then((data) => {
    if (!data) {
      return true
    }
    const responseData: any = data.data.value
    if (responseData.success) {
      store.globalSettings.setGlobalSettings(responseData.data)
    }
  })
}

const getNotifications = () => {
  $api($url.notification.notificationList, {
    body: {
      startRow: 0,
      endRow: 10
    }
  })
    .then((notifications) => {
      const success = $lodash.get(notifications, 'data.value.success')
      if (success) {
        const res: any = $lodash.get(notifications, 'data.value.data')
        const notify = res?.notification || []
        const unread = res?.unread || 0
        store.notification.setNotifications(notify)
        store.notification.setUnread(unread)
      }
    })
    .catch((e: any) => {
      if (process.client && typeof e.message === 'string') {
        $toast().error(e.message)
      }
    })
}

const checkOwner = (val: string | undefined) => {
  if (val !== undefined && val !== 'PARTNER') {
    router.push({
      // name: 'workspace-list'
      name: 'user-search-booking'
    })
    return false
  }
  return true
}
</script>
